import { useEffect, useState } from "react";
import { API, authFetch } from "@/App";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Brain, Target, Trophy, Package, CalendarRange, Gift, Wallet, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";

const formatINR = (paise) => {
  const r = (paise || 0) / 100;
  if (r >= 10000000) return `₹${(r / 10000000).toFixed(2)}Cr`;
  if (r >= 100000) return `₹${(r / 100000).toFixed(2)}L`;
  if (r >= 1000) return `₹${(r / 1000).toFixed(1)}K`;
  return `₹${r.toFixed(0)}`;
};

const fmtMonth = (m) => {
  if (!m) return "—";
  try {
    const [y, mo] = m.split("-");
    return new Date(parseInt(y), parseInt(mo) - 1).toLocaleString("en-IN", { month: "short", year: "numeric" });
  } catch { return m; }
};

/**
 * <IntelligenceSnapshot>
 * One-tile overview pulled from /api/analytics/intelligence-snapshot.
 * Combines: target health, top performers, top product/month, scheme count,
 * receivables roll-up. Each sub-card deep-links to its full module so admins
 * can drill in.
 */
export function IntelligenceSnapshot() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(`${API}/analytics/intelligence-snapshot`);
        const j = await res.json();
        if (!cancelled && j.success) setData(j.data);
      } catch { /* swallow */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <Card className="p-5 animate-pulse" data-testid="intelligence-snapshot-loading">
      <div className="h-4 w-48 bg-slate-200 rounded mb-3" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[1,2,3,4].map((i) => <div key={i} className="h-20 bg-slate-100 rounded" />)}
      </div>
    </Card>
  );

  if (!data) return null;

  const targetHealth = data.targets.avg_achievement_pct;
  const targetColor = targetHealth >= 70 ? "emerald" : targetHealth >= 40 ? "amber" : "red";

  return (
    <Card className="p-5 border-2 border-violet-100 bg-gradient-to-br from-violet-50/30 via-white to-blue-50/20" data-testid="intelligence-snapshot">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-900 flex items-center gap-2">
          <Brain className="w-5 h-5 text-violet-600" />
          Intelligence Snapshot
          <Badge variant="outline" className="text-[10px] bg-violet-100 text-violet-700 border-violet-200">Live</Badge>
        </h3>
        <Link to="/analytics" className="text-xs text-violet-600 hover:underline flex items-center gap-0.5" data-testid="snapshot-deeplink-analytics">
          Full analytics <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {/* Targets */}
        <Link to="/targets" className="block" data-testid="snapshot-targets">
          <div className={`p-3 rounded-lg border bg-${targetColor}-50 border-${targetColor}-200 hover:shadow-md transition cursor-pointer`}>
            <div className="flex items-center justify-between mb-1">
              <Target className={`w-4 h-4 text-${targetColor}-600`} />
              <Badge variant="outline" className="text-[9px] py-0">{data.targets.active_count} active</Badge>
            </div>
            <p className={`text-2xl font-bold text-${targetColor}-700`}>{targetHealth}%</p>
            <p className="text-[10px] text-slate-600 mt-0.5">Avg achievement</p>
            <div className="flex gap-1.5 mt-1.5">
              {data.targets.on_track > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-emerald-700"><CheckCircle2 className="w-2.5 h-2.5" /> {data.targets.on_track} on-track</span>
              )}
              {data.targets.at_risk > 0 && (
                <span className="inline-flex items-center gap-0.5 text-[10px] text-red-700"><AlertTriangle className="w-2.5 h-2.5" /> {data.targets.at_risk} at risk</span>
              )}
            </div>
          </div>
        </Link>

        {/* Top performer */}
        <Link to="/leaderboard" className="block" data-testid="snapshot-leaderboard">
          <div className="p-3 rounded-lg border bg-amber-50 border-amber-200 hover:shadow-md transition cursor-pointer">
            <div className="flex items-center justify-between mb-1">
              <Trophy className="w-4 h-4 text-amber-600" />
              <Badge variant="outline" className="text-[9px] py-0">{data.leaderboard.participants} active</Badge>
            </div>
            <p className="text-sm font-bold text-amber-900 truncate">
              {data.leaderboard.top_performers[0]?.name || "No leader yet"}
            </p>
            <p className="text-[10px] text-slate-600 mt-0.5">
              {data.leaderboard.top_performers[0] ? `${data.leaderboard.top_performers[0].orders} orders · ${formatINR(data.leaderboard.top_performers[0].collected_paise)} collected` : "This month"}
            </p>
            {data.leaderboard.top_performers[1] && (
              <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                #2 {data.leaderboard.top_performers[1].name}
              </p>
            )}
          </div>
        </Link>

        {/* Top product */}
        <Link to="/sales-forecast" className="block" data-testid="snapshot-top-product">
          <div className="p-3 rounded-lg border bg-violet-50 border-violet-200 hover:shadow-md transition cursor-pointer">
            <div className="flex items-center justify-between mb-1">
              <Package className="w-4 h-4 text-violet-600" />
              <Badge variant="outline" className="text-[9px] py-0">By Rev</Badge>
            </div>
            <p className="text-sm font-bold text-violet-900 truncate">
              {data.top_product?.product_name || "—"}
            </p>
            <p className="text-[10px] text-slate-600 mt-0.5">
              {data.top_product ? formatINR(data.top_product.revenue) : "No data"}
            </p>
            <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
              <CalendarRange className="w-2.5 h-2.5" /> Best month: {fmtMonth(data.top_month?.month)}
            </p>
          </div>
        </Link>

        {/* Receivables */}
        <Link to="/sales-analytics" className="block" data-testid="snapshot-receivables">
          <div className="p-3 rounded-lg border bg-red-50 border-red-200 hover:shadow-md transition cursor-pointer">
            <div className="flex items-center justify-between mb-1">
              <Wallet className="w-4 h-4 text-red-600" />
              <Badge variant="outline" className="text-[9px] py-0">{data.receivables.collection_pct}% collected</Badge>
            </div>
            <p className="text-base font-bold text-red-700 font-mono">
              {formatINR(data.receivables.outstanding)}
            </p>
            <p className="text-[10px] text-slate-600 mt-0.5">Outstanding</p>
            <div className="w-full h-1 bg-red-100 rounded-full mt-1.5 overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${data.receivables.collection_pct}%` }} />
            </div>
          </div>
        </Link>
      </div>

      {/* Footer strip: schemes + quick deep-links */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <Link to="/schemes" className="flex items-center gap-1 hover:text-[#8B1A1A]" data-testid="snapshot-schemes-link">
          <Gift className="w-3 h-3" /> <strong className="font-mono text-slate-900">{data.schemes.active}</strong> active schemes
        </Link>
        <span className="text-slate-300">·</span>
        <Link to="/analytics/cost-per-bag" className="hover:text-[#8B1A1A]" data-testid="snapshot-cost-link">
          View per-bag cost analytics
        </Link>
        <span className="text-slate-300">·</span>
        <Link to="/reports" className="hover:text-[#8B1A1A]">Export reports</Link>
      </div>
    </Card>
  );
}

export default IntelligenceSnapshot;
